import { useState } from "react";
import { Link, useLocation, useParams, useRoute } from "wouter";
import { useAuth } from "../../auth/AuthContext";
import { canManageFleet } from "../../auth/roleView";
import { isNotFound, isUnauthorized } from "../../api/errors";
import { useCorpus, useProject, useServices } from "../../api/queries";
import { ErrorState } from "../../components/AsyncStates";
import { Breadcrumb } from "../../components/Breadcrumb";
import { Button } from "../../components/Button";
import { DetailPageSkeleton } from "../../components/Skeleton";
import { TabPanel, Tabs, type TabItem } from "../../components/Tabs";
import { StatePill } from "../../components/StatePill";
import { DeployProjectDialog } from "./DeployProjectDialog";
import { CorpusPage } from "../corpus/CorpusPage";
import { ProjectOverviewTab } from "./ProjectOverviewTab";
import { ProjectServicesTab } from "./ProjectServicesTab";
import { ProjectSessionsTab } from "./ProjectSessionsTab";
import "./projects.css";

const TAB_ITEMS: TabItem[] = [
  { id: "overview", label: "Overview" },
  { id: "corpus", label: "Corpus" },
  { id: "sessions", label: "Sessions" },
  { id: "services", label: "Services" },
];

/**
 * A project's detail page (IA slice 3, docs/product/ui-information-architecture.md §3):
 * "project detail carries tabs: Overview · Corpus · Sessions · Services" — Develop's one
 * roof for developing an app. Mounted at four routes (`App.tsx`), one per tab, so the tab
 * and the URL are the SAME thing (a deep link to `/projects/:id/corpus` lands directly on
 * the Corpus tab, not a client-side-only tab state that a bookmark can't reach). The
 * active tab is derived from which route matched — never held as separate component
 * state that could drift from the URL (A-5).
 */
export function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { session } = useAuth();
  const [, navigate] = useLocation();

  const [matchCorpus] = useRoute("/projects/:projectId/corpus");
  const [matchSessions] = useRoute("/projects/:projectId/sessions");
  const [matchServices] = useRoute("/projects/:projectId/services");
  const activeTab = matchCorpus ? "corpus" : matchSessions ? "sessions" : matchServices ? "services" : "overview";

  const { data: project, isPending, isError, error, refetch } = useProject(projectId);
  const { data: corpus } = useCorpus(projectId ?? "");
  const { data: servicesPage } = useServices(projectId ?? "");
  const [deployDialogOpen, setDeployDialogOpen] = useState(false);

  // Mirrors the server's authz matrix (service:create/deploy is granted to
  // operator/admin only) — this only hides the control (S-8).
  const canManage = canManageFleet(session?.role);

  if (isPending) {
    return <DetailPageSkeleton cards={3} />;
  }

  if (isError) {
    if (isUnauthorized(error)) {
      return <DetailPageSkeleton cards={3} />;
    }
    if (isNotFound(error)) {
      return (
        <ErrorState
          title="Project not found"
          description="It may have been removed, or the link is out of date. Head back to Develop."
          action={
            <Link to="/projects" className="button button--secondary">
              Back to Develop
            </Link>
          }
        />
      );
    }
    return (
      <ErrorState
        description={error instanceof Error ? error.message : "Couldn't load this project."}
        onRetry={refetch}
      />
    );
  }

  if (!project) return null;

  const synced = Boolean(corpus?.sourceCommit);
  const undeployed = (servicesPage?.items ?? []).filter((s) => s.status === "created");

  function handleTabChange(id: string) {
    navigate(id === "overview" ? `/projects/${projectId}` : `/projects/${projectId}/${id}`);
  }

  return (
    <div className="project-detail">
      <Breadcrumb items={[{ label: "Develop", to: "/projects" }, { label: project.name }]} />

      <div className="project-detail__header">
        <div>
          <div className="project-detail__title-row">
            <h1 className="project-detail__title">{project.name}</h1>
            {synced && <StatePill label="Corpus applied" tone="positive" shape="dot" />}
          </div>
          <p className="project-detail__subtitle">
            Created by {project.createdBy || "—"} · updated {new Date(project.updatedAt).toLocaleString()}
          </p>
        </div>
        {canManage && (
          <div className="project-detail__deploy-action">
            <Button
              variant="primary"
              disabled={undeployed.length === 0}
              onClick={() => setDeployDialogOpen(true)}
            >
              {`Deploy ${project.name}`}
            </Button>
            {undeployed.length === 0 && (
              <p className="project-detail__deploy-hint">
                Every service in this project has already been deployed at least once.
              </p>
            )}
          </div>
        )}
      </div>

      <Tabs items={TAB_ITEMS} activeId={activeTab} onChange={handleTabChange} ariaLabel="Project detail views" />

      <TabPanel id="overview" activeId={activeTab}>
        <ProjectOverviewTab project={project} canManage={canManage} />
      </TabPanel>

      <TabPanel id="corpus" activeId={activeTab}>
        <CorpusPage showHeader={false} />
      </TabPanel>

      <TabPanel id="sessions" activeId={activeTab}>
        <ProjectSessionsTab project={project} canManage={canManage} />
      </TabPanel>

      <TabPanel id="services" activeId={activeTab}>
        <ProjectServicesTab project={project} canManage={canManage} />
      </TabPanel>

      {canManage && (
        <DeployProjectDialog
          open={deployDialogOpen}
          projectId={projectId ?? ""}
          projectName={project.name}
          services={undeployed}
          onClose={() => setDeployDialogOpen(false)}
        />
      )}
    </div>
  );
}
